import {
  SidePanelDS,
  TextCapitalized,
  Spacer,
  RemoveRedEyeOutlined,
  ButtonDS,
} from "@qivia/ui";
import { useMemo, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../../redux/hooks";
import { selectListAllVehiclesInDb } from "../../vehicles/vehiclesSlice";
import { DriversListType } from "../driversAPI";
import styled from "styled-components";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { selectDriversHistoryList } from "../../fleet/fleetSlice";
import { VehicleHistoryModal } from "../../sharedModals/vehicles/vehicleHistoryModal";

export const VehicleSection = (props: {
  driver: DriversListType;
  firstPath: string;
}) => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const vehicles = useAppSelector(selectListAllVehiclesInDb);
  const driversHistoryList = useAppSelector(selectDriversHistoryList);
  const [isVehicleHistoryModalOpen, setIsVehicleHistoryModalOpen] =
    useState(false);

  const vehicle = vehicles.find((v) => v.uuid === props.driver.vehicleUuid);

  const driverHistory = useMemo(
    () =>
      driversHistoryList
        .filter((dh) => dh.driverUuid === props.driver.uuid)
        .map((dh) => {
          const vehicle = vehicles.find((v) => v.uuid === dh.vehicleUuid);
          return {
            ...dh,
            vehicle: vehicle,
          };
        }),
    [driversHistoryList, props.driver.uuid, vehicles],
  );

  const handleClickVehicle = useCallback(() => {
    navigate(
      `/${props.firstPath}/vehicles/${props.driver.vehicleUuid}?fromNavigate=true`,
    );
  }, [navigate, props.firstPath, props.driver.vehicleUuid]);

  if (!vehicle) {
    return;
  }

  return (
    <SidePanelDS.Section verticalSpacing={1.5} horizontalSpacing={1.5}>
      <StyledTitle>
        <TextCapitalized>
          {t("drivers.sidePanel.driversHistory")}
        </TextCapitalized>
      </StyledTitle>
      <>
        <Spacer y={2} />
        <SidePanelDS.ActionBlock>
          <SidePanelDS.SectionedActionStripe
            left={
              <div>
                {vehicle.brand && vehicle.model && (
                  <>
                    <TextCapitalized>
                      <StyledRow>
                        <StyledBodyM>{vehicle.brand}</StyledBodyM>
                        <Spacer x={0.5} />
                        <StyledBodyS>{vehicle.model}</StyledBodyS>
                      </StyledRow>
                    </TextCapitalized>
                    <Spacer y={0.5} />
                  </>
                )}
                <StyledBodyM>{props.driver.registrationNumber}</StyledBodyM>
              </div>
            }
            right={
              <StyledLittleIcon>
                <RemoveRedEyeOutlined
                  key="vehicle"
                  onClick={handleClickVehicle}
                />
                <Spacer x={1} />
              </StyledLittleIcon>
            }
          />
        </SidePanelDS.ActionBlock>
        <Spacer y={1} />
        {driverHistory.length > 0 && (
          <>
            <ButtonDS
              sizeButton="S"
              format="hug"
              buttonType="secondary"
              text={t("drivers.sidepanel.viewDriversHistoryList")}
              leftIcon={<RemoveRedEyeOutlined />}
              onClick={() => setIsVehicleHistoryModalOpen(true)}
            />
            <VehicleHistoryModal
              visible={isVehicleHistoryModalOpen}
              onClose={() => setIsVehicleHistoryModalOpen(false)}
              driverHistory={driverHistory}
              driverName={props.driver.name}
            />
          </>
        )}
      </>
    </SidePanelDS.Section>
  );
};

const StyledTitle = styled.div`
  ${typographies["Body/L"]}
  color: ${colors["colors/text/darkGrey"]}
`;

const StyledLittleIcon = styled.div`
  display: flex;
  cursor: pointer;
  & svg {
    width: 1rem;
    height: 1rem;
  }
`;

const StyledBodyM = styled.div`
  ${typographies["Body/M"]}
`;

const StyledBodyS = styled.div`
  ${typographies["Body/S"]}
`;

const StyledRow = styled.div`
  display: flex;
  align-items: flex-end;
`;
